import { ArrowLeft, Bell, Building2, Check, ChevronDown, ChevronRight, KeyRound, Languages, LogOut, Menu as MenuIcon, Monitor, Moon, Rows3, Search, SpeakerSimpleHigh, SpeakerSimpleX, Sun } from '@/design-system/icons'
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, cn, Field, IconButton, Input, Kbd, Menu, Modal, notify, playAlertSound } from '@/design-system'

import { useT } from '@/i18n'
import { useNavBadges } from '@/navigation/badges'
import { useActiveNav } from '@/navigation/useNavigation'
import { authService } from '@/services/authService'
import { useAuth, useCan } from '@/stores/auth'
import { useUi } from '@/stores/ui'
import { pick } from '@/domain/extract'
import { InstallButton } from './AppInstall'
import { useTabs } from '@/stores/tabs'
import { MobileTabsButton } from './workspace/MobileTabs'
import { useTabLink } from './workspace/tabMeta'

/** True when the app runs installed (its own window, no browser bar) — no browser back button there. */
export const isInstalledApp = () =>
  typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || window.matchMedia?.('(display-mode: window-controls-overlay)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true)

/**
 * The browser's back, for the installed app (owner, 2026-10-01: "لما يعملو تسطيب للسيستم علي لاب توب ... مفيش زر
 * للعوده"): installed, the app has its own window and no browser bar, so the way back has to be ours.
 */
export function BackButton() {
  const { locale } = useT()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const menuOrigin = useUi((s) => s.menuOrigin)
  const setMobileNav = useUi((s) => s.setMobileNav)
  const setMenuBack = useUi((s) => s.setMenuBack)
  if (!isInstalledApp()) return null
  /*
   * On a phone, a page opened from the menu goes back to that menu, on the page's own section (owner, 2026-10-19:
   * «ما اعمل عوده من فوق بيطلعني ع سايد بار خالص مش القسم ال دخلت منه»); anything else goes back a page as before.
   */
  const back = () => {
    const phone = typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)').matches
    if (phone && menuOrigin && pathname === menuOrigin.split('?')[0]) { setMenuBack(true); setMobileNav(true) }
    else navigate(-1)
  }
  return <IconButton label={locale === 'ar' ? 'رجوع' : 'Back'} icon={ArrowLeft} className="rtl:[&_svg]:rotate-180" onClick={back} />
}

export function Breadcrumbs() {
  const { l, t } = useT()
  const active = useActiveNav()
  const tabLink = useTabLink()
  if (!active) return null
  const crumbs = [...active.trail.map((g) => l(g.label)), l(active.page.label)]
  return (
    <ol className="flex min-w-0 items-center gap-1 text-xs text-fg-muted" aria-label="Breadcrumb">
      <li className="hidden sm:block">
        <Link to="/" className="hover:text-fg" {...tabLink('/')}>
          {t('shell.home')}
        </Link>
      </li>
      {crumbs.map((c, i) => (
        <li key={i} className={i < crumbs.length - 1 ? 'hidden min-w-0 items-center gap-1 md:flex' : 'flex min-w-0 items-center gap-1'}>
          <ChevronRight size={12} className="hidden shrink-0 opacity-60 sm:block rtl:rotate-180" aria-hidden />
          <span className={i === crumbs.length - 1 ? 'truncate font-medium text-fg' : 'truncate'} aria-current={i === crumbs.length - 1 ? 'page' : undefined}>
            {c}
          </span>
        </li>
      ))}
    </ol>
  )
}

/** One floating card for every control on the bar: the same height, corners and soft shadow. */
const CARD = 'rounded-xl border border-border/70 bg-surface shadow-[0_1px_2px_rgba(16,24,40,0.04),0_6px_16px_-8px_rgba(16,24,40,0.10)] transition-[border-color,box-shadow,color] duration-150'
const BRANCH = cn(CARD, 'inline-flex h-9 min-w-0 max-w-[46vw] items-center gap-1.5 border-brand/25 bg-gradient-to-b from-brand-soft/60 to-brand-soft ps-1 pe-2 text-xs sm:h-10 sm:max-w-72 sm:shrink-0 sm:gap-2 sm:pe-3')

/** The branch as the bar shows it: its icon in a tile, «الفرع» quiet, the name bold. */
function BranchFace({ label, name }: { label: string; name: string }) {
  return (
    <>
      <span className="flex size-7 shrink-0 items-center justify-center rounded-[9px] bg-surface/90 text-brand-text shadow-sm sm:size-8 sm:rounded-[10px]"><Building2 size={15} aria-hidden /></span>
      <span className="hidden shrink-0 font-normal text-brand-text/60 sm:inline">{label}</span>
      <span className="truncate font-bold text-brand-text">{name}</span>
    </>
  )
}

export function Topbar({ onOpenCommand }: { onOpenCommand: () => void }) {
  const { t, locale } = useT()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const user = useAuth((s) => s.user)
  const logout = useAuth((s) => s.logout)
  const refresh = useAuth((s) => s.refresh)
  const { setMobileNav, theme, setTheme, setLocale, density, setDensity, errorSound, setErrorSound } = useUi()
  const badges = useNavBadges()
  const canNotifications = useCan('notifications:read')
  const [pwOpen, setPwOpen] = useState(false)
  const tabLink = useTabLink()

  const branches = useQuery({ queryKey: ['auth', 'branches'], queryFn: authService.branches, staleTime: 10 * 60_000, enabled: Boolean(user) })
  const branchName = (() => {
    const b = branches.data?.find((x) => Number(pick(x, 'branchID', 'branchId', 'id')) === user?.branchId)
    return b ? String(pick(b, 'branchName', 'name', 'branchNameAr', 'branchNameEn') ?? '') : null
  })()

  const switchBranch = async (id: number) => {
    try {
      await authService.changeBranch(id)
      await refresh()
      await qc.invalidateQueries()
      notify.success(t('shell.switchBranch'))
    } catch (e) {
      notify.error(e)
    }
  }

  const initials = (user?.displayName ?? user?.username ?? '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
  const unread = badges.notifications ?? 0

  return (
    <header className="sticky top-0 z-30 flex min-h-14 shrink-0 items-center gap-1.5 border-b border-border/60 bg-bg/75 px-2.5 pt-[env(safe-area-inset-top)] backdrop-blur-xl sm:min-h-16 sm:gap-2.5 sm:px-6">
      <IconButton className="md:hidden" label={t('shell.toggleSidebar')} icon={MenuIcon} onClick={() => setMobileNav(true)} />
      <BackButton />
      <div className="min-w-0 flex-1">
        <div className="hidden md:block"><Breadcrumbs /></div>
      </div>

      <button
        onClick={onOpenCommand}
        className={cn(CARD, 'hidden h-10 w-72 items-center gap-2.5 px-3.5 text-xs text-fg-subtle hover:border-border-strong md:flex lg:w-80 xl:w-96')}
      >
        <Search size={15} aria-hidden className="shrink-0 text-fg-muted" />
        <span className="flex-1 truncate text-start">{t('shell.commandHint')}</span>
        <Kbd>Ctrl</Kbd>
        <Kbd>K</Kbd>
      </button>
      <IconButton className="md:hidden" label={t('common.search')} icon={Search} onClick={onOpenCommand} />
      {/* phones: the open tabs live behind this button (no room for the strip) */}
      <MobileTabsButton onNewTab={onOpenCommand} />
      <InstallButton />

      {/*
        * The branch he works in now, always in sight — phones too, and with only one branch (owner, 2026-10-25: «تظهر
        * للموظفين فوق الفرع الحالي ال هما فيه»; 3020912 was put on a sheet from the wrong branch without anyone seeing it).
        */}
      {/*
        * Said in words and always there (owner, 2026-10-26: «الموظف يقدر يغير الفرع منين فوق ف سيستم عشان مش لاقيه»):
        * «الفرع: …» with a caret when it can be changed, and why it cannot when the account has one branch only.
        */}
      {(branches.data?.length ?? 0) <= 1 && Boolean(branchName || user?.branchId) && (
        <span className={cn(BRANCH, 'cursor-default')} title={locale === 'ar' ? 'حسابك على فيسوفت مسموح له بهذا الفرع فقط — لتغييره يجب إضافة الفرع إلى حسابك' : 'Your account is allowed this branch only'}>
          <BranchFace label={locale === 'ar' ? 'الفرع' : 'Branch'} name={branchName || `#${user?.branchId}`} />
        </span>
      )}
      {(branches.data?.length ?? 0) > 1 && (
        <Menu
          trigger={
            <button className={cn(BRANCH, 'hover:border-brand/40 hover:shadow-md')} aria-label={locale === 'ar' ? 'الفرع الحالي — اضغط لتغييره' : 'Current branch — click to change'} title={locale === 'ar' ? 'اضغط لتغيير الفرع' : 'Click to change the branch'}>
              <BranchFace label={locale === 'ar' ? 'الفرع' : 'Branch'} name={branchName ?? t('shell.branch')} />
              <ChevronDown size={14} aria-hidden className="shrink-0 text-brand-text/60" />
            </button>
          }
          items={(branches.data ?? []).map((b) => {
            const id = Number(pick(b, 'branchID', 'branchId', 'id'))
            return { label: String(pick(b, 'branchName', 'name') ?? id), icon: id === user?.branchId ? Check : undefined, onSelect: () => void switchBranch(id) }
          })}
        />
      )}

      {canNotifications && (
        // the badge counts Aliens OS alerts too (rarely Dispatch's): it opens the alerts center, where they are
        // (owner, 2026-10-01: "ظاهر ف نوتفكيشن فوق 224 نوتفكيشن بنضغط مفيش حاجه")
        <Link to="/notifications/alerts" {...tabLink('/notifications/alerts')} className={cn(CARD, 'relative inline-flex size-9 shrink-0 items-center justify-center text-fg-muted hover:text-fg sm:size-10')} aria-label={locale === 'ar' ? (unread ? `الإشعارات: ${unread} جديد` : 'الإشعارات') : unread ? `Notifications: ${unread} new` : 'Notifications'} title={unread ? String(unread > 999 ? '999+' : unread) : undefined}>
          <Bell size={18} aria-hidden />
          {unread > 0 && <span className="absolute end-2 top-2 size-2 rounded-full bg-brand ring-2 ring-surface sm:end-2.5 sm:top-2.5" aria-hidden />}
        </Link>
      )}

      <Menu
        trigger={
          <button className={cn(CARD, 'flex h-9 shrink-0 items-center gap-1.5 pe-1.5 ps-1 hover:border-border-strong sm:h-10 sm:gap-2 sm:pe-2.5')} aria-label={user?.displayName ?? user?.username ?? 'Account'}>
            <span className="flex size-7 shrink-0 items-center justify-center rounded-[9px] text-2xs font-bold text-white shadow-sm sm:size-8 sm:rounded-[10px]" style={{ background: 'linear-gradient(140deg,#e0383b,#ae1a1c)' }}>{initials}</span>
            <span className="hidden min-w-0 max-w-36 text-start leading-tight xl:block">
              <b className="block truncate text-[13px] font-semibold text-fg">{user?.displayName ?? user?.username}</b>
              <small className="block truncate text-[11px] text-fg-subtle" dir="ltr">{user?.username}</small>
            </span>
            <ChevronDown size={14} aria-hidden className="shrink-0 text-fg-subtle" />
          </button>
        }
        items={[
          { label: `${t('shell.theme')}: ${t('shell.themeLight')}`, icon: Sun, onSelect: () => setTheme('light'), hidden: theme === 'light' },
          { label: `${t('shell.theme')}: ${t('shell.themeDark')}`, icon: Moon, onSelect: () => setTheme('dark'), hidden: theme === 'dark' },
          { label: `${t('shell.theme')}: ${t('shell.themeSystem')}`, icon: Monitor, onSelect: () => setTheme('system'), hidden: theme === 'system' },
          { label: locale === 'ar' ? 'English' : 'العربية', icon: Languages, onSelect: () => setLocale(locale === 'ar' ? 'en' : 'ar') },
          { label: `${t('shell.density')}: ${density === 'compact' ? t('shell.densityComfortable') : t('shell.densityCompact')}`, icon: Rows3, onSelect: () => setDensity(density === 'compact' ? 'comfortable' : 'compact') },
          // The alert that plays on every error (owner, 2026-09-23) — muted per user, remembered on this device.
          {
            label: errorSound ? t('shell.errorSoundOff') : t('shell.errorSoundOn'),
            icon: errorSound ? SpeakerSimpleX : SpeakerSimpleHigh,
            onSelect: () => {
              setErrorSound(!errorSound)
              // Unmuting plays it once, so the user hears what they just turned on.
              if (!errorSound) playAlertSound({ enabled: true, pathname: '/', force: true })
            },
          },
          'separator',
          { label: t('auth.changePassword'), icon: KeyRound, onSelect: () => setPwOpen(true) },
          {
            label: t('auth.signOut'),
            icon: LogOut,
            danger: true,
            onSelect: async () => {
              await logout()
              qc.clear()
              useTabs.getState().reset()
              navigate('/login')
            },
          },
        ]}
      />
      <ChangePasswordModal open={pwOpen} onOpenChange={setPwOpen} />
    </header>
  )
}

function ChangePasswordModal({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useT()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [busy, setBusy] = useState(false)
  const submit = async () => {
    setBusy(true)
    try {
      await authService.changePassword(current, next)
      notify.success(t('auth.passwordChanged'))
      onOpenChange(false)
      setCurrent('')
      setNext('')
    } catch (e) {
      notify.error(e)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={t('auth.changePassword')}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={busy} disabled={!current || next.length < 6} onClick={submit}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={t('auth.currentPassword')} required>{(id) => <Input id={id} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />}</Field>
        <Field label={t('auth.newPassword')} required hint="≥ 6">{(id, d) => <Input id={id} aria-describedby={d} type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />}</Field>
      </div>
    </Modal>
  )
}
